import { useEffect, useRef, useState } from "react";

let activeWHEPConnection = null;

async function negotiateConnectionWithClientOffer(peerConnection, endpoint) {
	if (!endpoint || endpoint === "null") {
		console.error("Invalid endpoint provided.");
		return;
	}

	const offer = await peerConnection.createOffer();

	await peerConnection.setLocalDescription(offer);

	const ofr = await waitToCompleteICEGathering(peerConnection);
	if (!ofr) {
		throw Error("failed to gather ICE candidates for offer");
	}

	while (peerConnection.connectionState !== "closed") {
		const response = await postSDPOffer(endpoint, ofr.sdp);
		if (response.status === 201) {
			const answerSDP = await response.text();
			await peerConnection.setRemoteDescription(
				new RTCSessionDescription({ type: "answer", sdp: answerSDP })
			);
			return response.headers.get("Location");
		} else if (response.status === 405) {
			console.error("Update the URL passed into the WHIP or WHEP client");
		} else {
			const errorMessage = await response.text();
			console.error(errorMessage);
		}

		await new Promise((r) => setTimeout(r, 3000));
	}
}

async function postSDPOffer(endpoint, data) {
	return await fetch(endpoint, {
		method: "POST",
		mode: "cors",
		headers: {
			"content-type": "application/sdp",
		},
		body: data,
	});
}

async function waitToCompleteICEGathering(peerConnection) {
	return new Promise((resolve) => {
		/** Wait at most 1 second for ICE gathering. */
		setTimeout(function () {
			if (peerConnection.connectionState === "closed") {
				console.error(
					"Peer connection is closed, cannot access localDescription."
				);
				resolve(null);
				return;
			}
			resolve(peerConnection.localDescription);
		}, 1000);
		peerConnection.onicegatheringstatechange = (ev) =>
			peerConnection.iceGatheringState === "complete" &&
			resolve(peerConnection.localDescription);
	});
}
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const useWHEPClient = (endpoint: string, videoElement: HTMLVideoElement) => {
	const [stream, setStream] = useState(null);
	const peerConnectionRef = useRef<RTCPeerConnection | null>(null);

	useEffect(() => {
		if (!endpoint) return;
		setStream(new MediaStream());

		if (
			typeof window !== "undefined" &&
			typeof window.RTCPeerConnection === "undefined"
		) {
			console.error(
				"RTCPeerConnection is not available in this browser environment"
			);
			return;
		}

		if (activeWHEPConnection) {
			console.log(
				"Closing existing WHEP connection before creating a new one"
			);
			activeWHEPConnection.close();
			activeWHEPConnection = null;
		}

		try {
			const peerConnection = new RTCPeerConnection({
				iceServers: [
					{
						urls: "stun:stun.cloudflare.com:3478",
					},
				],
				bundlePolicy: "max-bundle",
			});

			activeWHEPConnection = peerConnection;
			peerConnectionRef.current = peerConnection;

			peerConnection.addTransceiver("video", {
				direction: "recvonly",
			});
			peerConnection.addTransceiver("audio", {
				direction: "recvonly",
			});

			peerConnection.ontrack = (event) => {
				const track = event.track;
				const currentTracks = stream.getTracks();
				const streamAlreadyHasVideoTrack = currentTracks.some(
					(track) => track.kind === "video"
				);
				const streamAlreadyHasAudioTrack = currentTracks.some(
					(track) => track.kind === "audio"
				);
				switch (track.kind) {
					case "video":
						if (streamAlreadyHasVideoTrack) {
							break;
						}
						stream.addTrack(track);
						break;
					case "audio":
						if (streamAlreadyHasAudioTrack) {
							break;
						}
						stream.addTrack(track);
						break;
					default:
						console.log("got unknown track " + track);
				}
			};

			peerConnection.addEventListener("connectionstatechange", (ev) => {
				if (peerConnection.connectionState !== "connected") {
					return;
				}

				if (!videoElement.srcObject) {
					videoElement.srcObject = stream;
				}
			});

			peerConnection.addEventListener("negotiationneeded", (ev) => {
				negotiateConnectionWithClientOffer(peerConnection, endpoint);
			});
		} catch (error) {
			console.error("Error creating RTCPeerConnection:", error);
			activeWHEPConnection = null;
		}

		return () => {
			if (peerConnectionRef.current) {
				peerConnectionRef.current.close();

				if (activeWHEPConnection === peerConnectionRef.current) {
					activeWHEPConnection = null;
				}
			}
			setStream(null);
		};
	}, [endpoint, videoElement]);

	useEffect(() => {
		if (stream && videoElement && !videoElement.srcObject) {
			videoElement.srcObject = stream;
		}
	}, [stream, videoElement]);

	const disconnectWatcher = () => {
		if (peerConnectionRef.current) {
			peerConnectionRef.current.close();

			if (activeWHEPConnection === peerConnectionRef.current) {
				activeWHEPConnection = null;
			}
		}
		setStream(null);

		if (videoElement?.srcObject) {
			const mediaStream = videoElement.srcObject as MediaStream;
			mediaStream.getTracks().forEach((track) => track.stop());
			videoElement.srcObject = null;
		}
	};

	return { disconnectWatcher };
};

export default useWHEPClient;
